import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarX, Loader2, LogOut, Music2, BookOpen, Timer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { STRINGS } from "@/lib/i18n";
import { Logo } from "@/components/Logo";
import AuthModal from "@/components/AuthModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import PayPalCheckout, { usePayPalConfig } from "@/components/PayPalCheckout";
import { FORMATS, FORMATS_EN, LEVELS, LEVELS_EN, STYLES, STYLES_EN } from "@/lib/data";

const MATERIAL_ICONS = [Music2, Music2, BookOpen, Timer];

export default function Portal() {
  const { user, logout } = useAuth();
  const { lang } = useLanguage();
  const t = STRINGS[lang].portal;
  const formats = lang === "en" ? FORMATS_EN : FORMATS;
  const levels = lang === "en" ? LEVELS_EN : LEVELS;
  const stylesList = lang === "en" ? STYLES_EN : STYLES;
  const navigate = useNavigate();
  const paypalConfig = usePayPalConfig();
  const [authOpen, setAuthOpen] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    if (!user) return;
    api
      .get("/bookings/mine")
      .then((res) => setBookings(res.data))
      .catch(() => setBookings([]));
  }, [user]);

  const cancel = async (id) => {
    setCancelling(id);
    try {
      await api.post(`/bookings/${id}/cancel`);
      setBookings((list) => list.map((b) => (b.id === id ? { ...b, status: "cancelado" } : b)));
      toast.success(t.cancelled);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancelling(null);
    }
  };

  const labelFor = (list, id) => list.find((item) => item.id === id)?.name || id;
  const formatDate = (iso) => {
    const [y, m, d] = iso.split("-");
    return lang === "en" ? `${m}/${d}/${y}` : `${d}/${m}/${y}`;
  };

  const active = (bookings || []).filter((b) => b.status === "confirmado");
  const next = active.find((b) => `${b.date}T${b.time}` >= new Date().toISOString().slice(0, 16));
  const packages = paypalConfig?.packages ? Object.entries(paypalConfig.packages) : [];

  return (
    <div className="min-h-screen bg-[#05070D] text-slate-100" data-testid="student-portal">
      <header className="border-b border-white/5 bg-[#0B101C]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="portal-home-link">
            <Logo />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {user && (
              <button
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                data-testid="portal-logout-button"
                className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors duration-300 hover:border-blue-500/50 hover:text-blue-300"
              >
                <LogOut className="h-3.5 w-3.5" /> {t.logout}
              </button>
            )}
          </div>
        </div>
      </header>

      {user === null ? (
        <div className="flex h-[60vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
        </div>
      ) : user === false ? (
        <div className="mx-auto flex max-w-md flex-col items-center px-5 py-32 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{t.overline}</p>
          <h1 className="mt-4 font-serif text-3xl font-light text-slate-100">{t.lockedTitle}</h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">{t.lockedCopy}</p>
          <button
            onClick={() => setAuthOpen(true)}
            data-testid="portal-open-login-button"
            className="mt-8 rounded-full bg-blue-400 px-8 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#05070D] transition-colors duration-300 hover:bg-blue-300"
          >
            {t.lockedCta}
          </button>
        </div>
      ) : (
        <main className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{t.overline}</p>
            <h1 className="mt-4 font-serif text-3xl font-light tracking-tight text-slate-100 sm:text-4xl" data-testid="portal-greeting">
              {t.hello} <em className="italic text-blue-400">{user.name.split(" ")[0]}</em>.
            </h1>
            {next && (
              <p className="mt-3 text-sm text-slate-400" data-testid="portal-next-lesson">
                {t.nextLesson} <span className="text-blue-300">{formatDate(next.date)} · {next.time}</span> —{" "}
                {labelFor(formats, next.format)}
              </p>
            )}
          </motion.div>

          <section className="mt-14" data-testid="portal-bookings-section">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-slate-100">{t.bookingsTitle}</h2>
              <Link
                to="/#agendar"
                data-testid="portal-new-booking-link"
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-400 transition-colors duration-300 hover:text-blue-300"
              >
                {t.newBooking}
              </Link>
            </div>

            {bookings === null ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center" data-testid="portal-bookings-empty">
                <p className="text-sm text-slate-400">{t.emptyBookings}</p>
                <Link
                  to="/#agendar"
                  className="mt-6 inline-block rounded-full bg-blue-400 px-7 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[#05070D] transition-colors duration-300 hover:bg-blue-300"
                >
                  {t.bookNow}
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {bookings.map((booking) => (
                  <article
                    key={booking.id}
                    className="rounded-2xl border border-white/10 bg-[#0B101C]/80 p-6 backdrop-blur-xl"
                    data-testid={`portal-booking-card-${booking.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-serif text-xl text-blue-50">{labelFor(formats, booking.format)}</p>
                        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                          {labelFor(levels, booking.level)} · {labelFor(stylesList, booking.style)}
                        </p>
                      </div>
                      <span
                        className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
                          booking.status === "confirmado"
                            ? "border-blue-500/30 bg-blue-500/10 text-blue-300"
                            : "border-white/10 text-slate-500"
                        }`}
                        data-testid={`portal-booking-status-${booking.id}`}
                      >
                        {t.statusMap[booking.status] || booking.status}
                      </span>
                    </div>
                    <p className="mt-4 text-sm text-slate-300">
                      {formatDate(booking.date)} · {booking.time}
                    </p>
                    {booking.status === "confirmado" && (
                      <button
                        onClick={() => cancel(booking.id)}
                        disabled={cancelling === booking.id}
                        data-testid={`portal-booking-cancel-${booking.id}`}
                        className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500 transition-colors duration-300 hover:text-red-400 disabled:opacity-50"
                      >
                        {cancelling === booking.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarX className="h-3.5 w-3.5" />}
                        {t.cancel}
                      </button>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="mt-16" data-testid="portal-packages-section">
            <h2 className="mb-2 font-serif text-2xl text-slate-100">{t.planTitle}</h2>
            <p className="mb-6 text-sm text-slate-400">{t.planCopy}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {packages.map(([packageId, pkg]) => (
                <article
                  key={packageId}
                  className="rounded-2xl border border-blue-500/25 bg-[#0B101C]/80 p-6 backdrop-blur-xl shadow-[0_0_35px_rgba(59,130,246,0.08)]"
                  data-testid={`portal-package-${packageId}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-serif text-xl text-blue-50">{t.planName[packageId] || pkg.name}</p>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        {pkg.lessons} {t.planPerks}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-serif text-3xl text-blue-300">US$ {Number(pkg.price)}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{t.perMonth}</p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <PayPalCheckout description={`IGL — ${t.planName[packageId] || pkg.name}`} amount={pkg.price} testid={`paypal-package-${packageId}`} />
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-16" data-testid="portal-materials-section">
            <h2 className="mb-6 font-serif text-2xl text-slate-100">{t.materialsTitle}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {t.materials.map((material, i) => {
                const Icon = MATERIAL_ICONS[i];
                return (
                  <div
                    key={material.title}
                    className="flex items-center gap-5 rounded-2xl border border-white/10 bg-[#0B101C]/80 p-5"
                    data-testid={`portal-material-${i}`}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-blue-500/25 bg-blue-500/5">
                      <Icon className="h-5 w-5 text-blue-400" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-slate-200">{material.title}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{material.meta}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </main>
      )}

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
